export const sleep = (t: number): Promise<void> => {
  return new Promise(resolve => {
    setTimeout(resolve, t)
  })
}

export const once = <Args extends unknown[], R>(
  fn: (...args: Args) => R | Promise<R>,
): ((...args: Args) => Promise<R>) => {
  let p: Promise<R> | null = null
  return async (...args: Args) => {
    if (p !== null) return p
    p = Promise.resolve(fn(...args))
    const ans = await p
    p = null
    return ans
  }
}

export const retry = <Args extends unknown[], R>(
  fn: (...args: Args) => R | Promise<R>,
  max = 0,
  duration = 100,
): ((...args: Args) => Promise<R>) => {
  return async (...args: Args) => {
    for (let i = 0; i <= max; i++) {
      try {
        return await fn(...args)
      } catch (err) {
        if (i === max) throw err
        await sleep(duration)
      }
    }
    throw new Error('retry: unreachable')
  }
}

/**
 * continuous running task,
 * 1. will only continue after finishing current task
 * 2. only run once even if get many requests during current task execution period
 */
export const continuous = <Args extends unknown[]>(
  fn: (...args: Args) => unknown,
): ((...args: Args) => void) => {
  let nextFlag = false
  let p: Promise<unknown> | null = null

  const run = async (...args: Args): Promise<void> => {
    if (nextFlag && p === null) {
      nextFlag = false

      try {
        p = Promise.resolve(fn(...args))
        await p
      } catch (_err) {
        //
      }

      p = null
      return run(...args)
    }
  }

  return (...args: Args) => {
    nextFlag = true
    run(...args)
  }
}

interface ConcurTask<R> {
  resolve: (v: R) => void
  reject: (e: unknown) => void
  fn: () => R | Promise<R>
}

export type ConcurrentRunner = <R>(fn: () => R | Promise<R>) => Promise<R>

export const concurrent = ({ max = 1024 }: { max?: number } = {}): ConcurrentRunner => {
  const taskQueue: ConcurTask<any>[] = []
  let con = 0

  const runTask = async (): Promise<void> => {
    if (con >= max) return
    if (!taskQueue.length) return

    const next = taskQueue.shift() as ConcurTask<any>
    const { resolve, reject, fn } = next

    con++
    try {
      resolve(await fn())
    } catch (err) {
      reject(err)
    }
    con--
    runTask()
  }

  return <R>(fn: () => R | Promise<R>) => {
    return new Promise<R>((resolve, reject) => {
      taskQueue.push({ resolve, reject, fn } as ConcurTask<R>)
      runTask()
    })
  }
}

export const bindOnConcurrent = <Args extends unknown[], R>(
  concur: ConcurrentRunner,
  fn: (...args: Args) => R | Promise<R>,
): ((...args: Args) => Promise<R>) => {
  return (...args: Args) => {
    return concur(() => {
      return fn(...args)
    })
  }
}

interface DelayTask<R> {
  resolve: (v: R) => void
  reject: (e: unknown) => void
  args: unknown[]
  fn: (...args: unknown[]) => R | Promise<R>
}

export const delayQueue = <Args extends unknown[], R>(
  fn: (...args: Args) => R | Promise<R>,
  delayTime: number,
): ((...args: Args) => Promise<R>) => {
  const queue: DelayTask<R>[] = []
  let flag = false

  const handleQueue = async (): Promise<void> => {
    if (queue.length === 0 || flag) return
    flag = true

    const first = queue.shift() as DelayTask<R>
    try {
      const ans = await first.fn(...(first.args as Args))
      first.resolve(ans)
    } catch (err) {
      first.reject(err)
    }

    await sleep(delayTime)

    flag = false
    handleQueue()
  }

  return (...args: Args) => {
    return new Promise<R>((resolve, reject) => {
      queue.push({
        resolve,
        reject,
        args,
        fn: fn as (...args: unknown[]) => R | Promise<R>,
      })

      handleQueue()
    })
  }
}
